import { Button, Card, Link, Modal, Space, Select, Message, Grid, Table } from "@arco-design/web-react";
import { EnumSelect } from "@/components/Enum";
import { StatusDef } from "@/enums/status";
import SaleOrderLink from "@/components/SaleOrderLink";
import Reference from "@/components/Reference";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Number } from "@/components/Numeric";
import { ColorfulTable } from "@/components/ColorfulTable";
import { PresetColorPicker } from "@/components/PresetColorPicker";
import { VendorSelect } from "@/components/VendorSelect";
import { InventoryAPI, ItemAPI } from "@/apis/material";
import { ItemInfoLink } from "@/components/ItemInfoLink";
import { NavLink } from "react-router-dom";
import _ from "lodash";
import { FileUtil, UserProfile, UserProfileType } from "@/utils";

const Row = Grid.Row;

const CommittedLink = ({ itemId, children }) => {
    const [visible, setVisible] = useState(false);
    const [data, setData] = useState();

    const getCommitted = async () => {
        const values = await InventoryAPI.getItemCommitted(itemId);
        setData(values);
    }

    return (
        <>
            <Link onClick={() => {
                getCommitted();
                setVisible(true);
            }}>{children}</Link>
            <Modal visible={visible}
                title="Item Committed"
                style={{ width: 600 }}
                footer={
                    <Button type="primary" onClick={() => setVisible(false)}>Close</Button>
                }
                onCancel={() => setVisible(false)}>
                <Table columns={[
                    { title: "Type", dataIndex: "type" },
                    {
                        title: "SO# / WO#", render: (col, record) => record.type === "SO"
                            ? <SaleOrderLink soId={record.sourceId}>{record.sourceNo}</SaleOrderLink>
                            : <NavLink to={`/workspace/manufacturing/work-order/${record.sourceId}`}>{record.sourceNo}</NavLink>
                    },
                    { title: "Customer / BOM", dataIndex: "name" },
                    { title: "Quantity", render: (col, record) => <Number value={record.quantity} /> }
                ]} data={data} rowKey={(record) => `${record.type}-${record.sourceId}`} />
            </Modal>
        </>
    )
}

const OnOrderLink = ({ itemId, children }) => {
    const [visible, setVisible] = useState(false);
    const [data, setData] = useState();

    const getDetails = async () => {
        const values = await InventoryAPI.getItemOnOrder(itemId);
        setData(values);
    }

    return (
        <>
            <Link onClick={() => {
                getDetails();
                setVisible(true);
            }}>{children}</Link>
            <Modal visible={visible}
                title="Item On Order"
                style={{ width: 600 }}
                footer={
                    <Button type="primary" onClick={() => setVisible(false)}>Close</Button>
                }
                onCancel={() => setVisible(false)}>
                <Table columns={[
                    { title: "Type", dataIndex: "type" },
                    {
                        title: "PO# / WO#", render: (col, record) => record.type === "PO"
                            ? <NavLink to={`/workspace/purchasing/po/${record.sourceId}`}>{record.sourceNo}</NavLink>
                            : <NavLink to={`/workspace/manufacturing/work-order/${record.sourceId}`}>{record.sourceNo}</NavLink>
                    },
                    { title: "Vendor / BOM", dataIndex: "name" },
                    { title: "Quantity", render: (col, record) => <Number value={record.quantity} /> }
                ]} data={data} rowKey={(record) => `${record.type}-${record.sourceId}`} />
            </Modal>
        </>
    )
}

const AllColumns = [
    { label: "Item Number", value: "itemNumber" },
    { label: "Item Name", value: "itemName" },
    { label: "Item Description", value: "description" },
    { label: "Inventory OnHand", value: "onHand" },
    { label: "Inventory NotAvailable", value: "notAvailable" },
    { label: "Inventory Committed", value: "committed" },
    { label: "Inventory Available", value: "available" },
    { label: "Inventory On Order", value: "onOrder" },
    { label: "Future Inventory", value: "future" },
    { label: "Last 1 Month Fulfillment", value: "last1Fulfillment" },
    { label: "Last 2 Month Fulfillment", value: "last2Fulfillment" },
    { label: "Last 3 Month Fulfillment", value: "last3Fulfillment" },
    { label: "Last 6 Month Fulfillment", value: "last6Fulfillment" },
    { label: "Last 12 Month Fulfillment", value: "last12Fulfillment" },
    { label: "Next Receiving", value: "nextComingPO" },
    { label: "Safety", value: "safety" },
];

export const Inventory = () => {
    const [items, setItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [status, setStatus] = useState(1);
    const [data, setData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [selectedCols, setSelectedCols] = useState(AllColumns.map(col => col.value));
    const [vendor, setVendor] = useState();
    const [preferredVendorId, setPreferredVendorId] = useState();
    const [safetyColor, setSafetyColor] = useState();
    const [last1FulfillmentColor, setLast1FulfillmentColor] = useState();
    const [last2FulfillmentColor, setLast2FulfillmentColor] = useState();
    const [last3FulfillmentColor, setLast3FulfillmentColor] = useState();
    const [last6FulfillmentColor, setLast6FulfillmentColor] = useState();
    const [last12FulfillmentColor, setLast12FulfillmentColor] = useState();

    const saveProfile = () => {
        UserProfile.saveProfile(UserProfileType.INVENTORY, {
            selectedCols,
            safetyColor,
            last1FulfillmentColor,
            last2FulfillmentColor,
            last3FulfillmentColor,
            last6FulfillmentColor,
            last12FulfillmentColor,
        });
    }

    const allColumns = useMemo(() => [
        {
            title: "Item Number",
            dataIndex: "itemNumber",
            fixed: "left",
            width: 100,
            render: (col, record) => <ItemInfoLink value={record.itemId}>{record.itemNo}</ItemInfoLink>,
            sorter: (a, b) => {
                if (a.itemNo > b.itemNo) {
                    return 1;
                } else if (a.itemNo < b.itemNo) {
                    return -1;
                } else {
                    return 0;
                }
            }
        },
        {
            title: "Item Name",
            dataIndex: "itemName",
            fixed: "left",
            width: 130,
            sorter: (a, b) => {
                if (a.itemName > b.itemName) {
                    return 1;
                } else if (a.itemName < b.itemName) {
                    return -1;
                } else {
                    return 0;
                }
            }
        },
        {
            title: "Item Description",
            dataIndex: "description",
            fixed: "left",
            width: 150
        },
        {
            title: "Inventory OnHand",
            dataIndex: "onHand",
            render: (col, record) => <Number value={record.onHand} />,
            sorter: (a, b) => a.onHand - b.onHand,
        },
        {
            title: "Inventory NotAvailable",
            dataIndex: "notAvailable",
            render: (col, record) => <Number value={record.notAvailable} />,
            sorter: (a, b) => a.notAvailable - b.notAvailable,
        },
        {
            title: "Inventory Committed",
            dataIndex: "committed",
            render: (col, record) => <CommittedLink itemId={record.itemId}>
                <Number value={record.committed} />
            </CommittedLink >,
            sorter: (a, b) => a.committed - b.committed
        },
        {
            title: "Inventory Available",
            dataIndex: "available",
            render: (col, record) => <Number value={record.available} />,
            sorter: (a, b) => a.available - b.available
        },
        {
            title: "Inventory On Order",
            dataIndex: "onOrder",
            render: (col, record) =>
                <OnOrderLink itemId={record.itemId}>
                    <Number value={record.onOrder} />
                </OnOrderLink>,
            sorter: (a, b) => a.onOrder - b.onOrder
        },
        {
            title: "Future Inventory",
            dataIndex: "future",
            render: (col, record) => <Number value={record.future} />,
            sorter: (a, b) => a.future - b.future,
        },
        {
            title: <div>
                <div>Last 1 Month Fulfillment</div>
                <PresetColorPicker
                    value={last1FulfillmentColor}
                    onChange={val => {
                        setLast1FulfillmentColor(val);
                    }}
                />
            </div>,
            dataIndex: "last1Fulfillment",
            bgColor: last1FulfillmentColor,
            colorful: (record) => record.last1Fulfillment > record.future,
            render: (col, record) => <Number value={record.last1Fulfillment} />
        },
        {
            title: <div>
                <div>Last 2 Month Fulfillment</div>
                <PresetColorPicker
                    value={last2FulfillmentColor}
                    onChange={val => {
                        setLast2FulfillmentColor(val);
                    }}
                />
            </div>,
            dataIndex: "last2Fulfillment",
            bgColor: last2FulfillmentColor,
            colorful: (record) => record.last2Fulfillment > record.future,
            render: (col, record) => <Number value={record.last2Fulfillment} />
        },
        {
            title: <div>
                <div>Last 3 Month Fulfillment</div>
                <PresetColorPicker
                    value={last3FulfillmentColor}
                    onChange={val => {
                        setLast3FulfillmentColor(val);
                    }}
                />
            </div>,
            dataIndex: "last3Fulfillment",
            bgColor: last3FulfillmentColor,
            colorful: (record) => record.last3Fulfillment > record.future,
            render: (col, record) => <Number value={record.last3Fulfillment} />
        },
        {
            title: <div>
                <div>Last 6 Month Fulfillment</div>
                <PresetColorPicker
                    value={last6FulfillmentColor}
                    onChange={val => {
                        setLast6FulfillmentColor(val);
                    }}
                />
            </div>,
            dataIndex: "last6Fulfillment",
            bgColor: last6FulfillmentColor,
            colorful: (record) => record.last6Fulfillment > record.future,
            render: (col, record) => <Number value={record.last6Fulfillment} />
        },
        {
            title: <div>
                <div>Last 12 Month Fulfillment</div>
                <PresetColorPicker
                    value={last12FulfillmentColor}
                    onChange={val => {
                        setLast12FulfillmentColor(val);
                    }}
                />
            </div>,
            dataIndex: "last12Fulfillment",
            bgColor: last12FulfillmentColor,
            colorful: (record) => record.last12Fulfillment > record.future,
            render: (col, record) => <Number value={record.last12Fulfillment} />
        },
        {
            title: "Next Receiving",
            dataIndex: "nextComingPO",
            render: (col, record) => record.nextComingPO ? <Space direction="vertical">
                <span>Qty: <Number value={record.nextComingPO?.quantity}></Number></span>
                <span>ETA: {record.nextComingPO?.eta}</span>
                <span>PO#: {record.nextComingPO?.poNo}</span>
                <span>Reference: <Reference value={record.nextComingPO?.references} /></span>
            </Space> : "",
        },
        {
            title: <div>
                <div>Safety</div>
                <PresetColorPicker
                    value={safetyColor}
                    onChange={val => {
                        setSafetyColor(val);
                    }} />
            </div>,
            dataIndex: "safety",
            bgColor: safetyColor,
            colorful: (record) => record.safety > record.available,
            render: (col, record) => <Number value={record.safety} />,
        },
    ], [
        last1FulfillmentColor,
        last2FulfillmentColor,
        last3FulfillmentColor,
        last6FulfillmentColor,
        last12FulfillmentColor,
        safetyColor
    ]);

    const fillColumns = useCallback(() => {
        const cols = allColumns.filter(col => selectedCols.includes(col.dataIndex));
        setColumns(cols);
    }, [selectedCols, allColumns]);

    const getData = useCallback(async (vendor, preferredVendorId, itemIds, status) => {
        const values = await InventoryAPI.getItems({ vendor, preferredVendorId, itemIds, status });
        const items = _.sortBy(values.records, "itemNo");
        setData(items);
    }, [])

    const exportData = async () => {
        const res = await InventoryAPI.exportData({ vendor, preferredVendorId, itemIds: selectedItems, status });
        FileUtil.download(res.data, "inventory.xlsx");
    }

    useEffect(() => {
        const loadProfile = async () => {
            const profile = await UserProfile.getProfile(UserProfileType.INVENTORY);
            if (profile) {
                if (profile.selectedCols != null || profile.selectedCols.length > 0) {
                    setSelectedCols(profile.selectedCols);
                } else {
                    setSelectedCols(AllColumns.map(col => col.value));
                }
                profile.safetyColor && setSafetyColor(profile.safetyColor);
                profile.last1FulfillmentColor && setLast1FulfillmentColor(profile.last1FulfillmentColor);
                profile.last2FulfillmentColor && setLast2FulfillmentColor(profile.last2FulfillmentColor);
                profile.last3FulfillmentColor && setLast3FulfillmentColor(profile.last3FulfillmentColor);
                profile.last6FulfillmentColor && setLast6FulfillmentColor(profile.last6FulfillmentColor);
                profile.last12FulfillmentColor && setLast12FulfillmentColor(profile.last12FulfillmentColor);
            }
        }
        loadProfile();
    }, [])

    useEffect(() => {
        const getItems = async () => {
            const values = await ItemAPI.actives();
            setItems(values);
        }
        getItems();
    }, []);

    useEffect(() => {
        fillColumns();
    }, [fillColumns]);

    useEffect(() => {
        getData(null, null, null, 1);
    }, [getData]);

    return (
        <Card title="Inventory" extra={
            <Space>
                <VendorSelect
                    style={{ width: 160 }}
                    placeholder="Vendor"
                    allowClear
                    value={vendor}
                    onChange={(val) => {
                        setVendor(val);
                    }}
                />
                <VendorSelect
                    style={{ width: 160 }}
                    placeholder="Preferred Vendor"
                    allowClear
                    value={preferredVendorId}
                    onChange={(val) => {
                        setPreferredVendorId(val);
                    }}
                />
                <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    placeholder="Select items..."
                    maxTagCount={1}
                    style={{ width: 200 }}
                    value={selectedItems}
                    filterOption={(inputValue, option) =>
                        option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                    }
                    onChange={(value) => {
                        setSelectedItems(value);
                    }}
                    options={_.sortBy(items, "itemName").map(item => ({
                        label: item.itemName,
                        value: item.id
                    }))}
                />
                <EnumSelect enumObj={StatusDef} allowAll placeholder="Status" value={status} onChange={(val) => {
                    setStatus(val);
                }} />
                <Button type="primary" onClick={() => {
                    getData(vendor, preferredVendorId, selectedItems, status);
                }}>Search</Button>
                <Button type="primary" onClick={() => exportData()}>Export</Button>
                <Button type="primary" onClick={() => {
                    saveProfile();
                    Message.success("Profile Saved");
                }}>Save Profile</Button>
            </Space>
        }>
            <Row justify="end">
                <Space>
                    <Select
                        mode="multiple"
                        bordered={false}
                        placeholder="Columns"
                        triggerProps={{
                            autoAlignPopupWidth: false,
                            autoAlignPopupMinWidth: true,
                        }}
                        value={selectedCols}
                        renderTag={(props, index) => {
                            return index === 0 ? <span style={{ margin: "0 5px" }}>Columns</span> : null;
                        }}
                        onChange={(selected) => {
                            setSelectedCols(selected);
                        }}
                        options={AllColumns}
                    />
                </Space>
            </Row>
            <ColorfulTable
                columns={columns}
                scroll={{ x: 1600 }}
                rowKey="itemId"
                data={data}
            />
        </Card >
    );
}